import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { getProjectModuleCapabilities } from '../../config/projectCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  addProjectMembers,
  getProjectDetails,
} from '../../services/projectService';
import type {
  ProjectDetails,
  ProjectMemberDraft,
  ProjectMemberRole,
} from '../../types/project';
import { PROJECT_MEMBER_ROLES } from '../../types/project';
import { formatDate } from '../../utils/dateTime';
import { runOperation } from '../../utils/operationFeedback';
import {
  loadOrganizationEmployeeOptions,
  loadTeamEmployeeOptions,
} from '../../utils/projectEmployeeOptions';
import { projectErrorMessage } from '../../utils/projectErrors';
import {
  nextProjectDraftKey,
  projectMemberConflict,
  projectMemberRoleLabel,
  toProjectMembersAddPayload,
} from '../../utils/projectForm';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'ProjectEdit'>;

/**
 * Edit Project: adds members to an already-saved project. The project and its
 * current members are loaded from the API; existing members are shown but not
 * changed, and only the newly added members are sent on save.
 */
export function ProjectEditScreen({ navigation, route }: Props) {
  const { projectId, projectName: routeProjectName } = route.params;
  const auth = useAuth();
  const capabilities = getProjectModuleCapabilities(auth.permissions, auth.roles);
  const teamOnly = capabilities.scope === 'CREATOR';
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.xs,
      },
      hint: { ...typography.caption, color: palette.textSecondary },
      error: { ...typography.body, color: palette.error },
      label: { ...typography.caption, color: palette.textMuted },
      value: { ...typography.body, color: palette.textPrimary },
      memberName: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '600' as const,
        flex: 1,
      },
      memberRole: { ...typography.caption, color: palette.textSecondary },
      chip: {
        borderWidth: 1,
        borderColor: palette.border,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.surface,
      },
      chipActive: {
        borderWidth: 1,
        borderColor: palette.brand,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.brand,
      },
      chipText: { ...typography.caption, color: palette.textPrimary },
      chipTextActive: {
        ...typography.caption,
        color: palette.white,
        fontWeight: '700' as const,
      },
      remove: { ...typography.caption, color: palette.error, fontWeight: '700' as const },
      loading: { ...typography.body, color: palette.textSecondary },
    }),
    [palette],
  );

  const [project, setProject] = useState<ProjectDetails | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [newMembers, setNewMembers] = useState<ProjectMemberDraft[]>([]);
  const [pendingEmployee, setPendingEmployee] = useState<SelectorOption | null>(
    null,
  );
  const [pendingRole, setPendingRole] = useState<ProjectMemberRole | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const loadProject = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setProject(await getProjectDetails(projectId));
    } catch (caught) {
      setLoadError(projectErrorMessage(caught, 'Unable to load the project.'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const canEdit = capabilities.canEditProjectMembers;
  useEffect(() => {
    if (canEdit) loadProject();
  }, [canEdit, loadProject]);

  const existingMembers = useMemo(() => project?.members ?? [], [project]);

  // Hide employees who are already on the project or queued to be added.
  const loadOptions = useCallback(
    async (search: string) => {
      const options = await (teamOnly
        ? loadTeamEmployeeOptions(search)
        : loadOrganizationEmployeeOptions(search));
      return options.filter(
        option => !projectMemberConflict(option.id, existingMembers, newMembers),
      );
    },
    [teamOnly, existingMembers, newMembers],
  );

  function addMember(employee: SelectorOption, role: ProjectMemberRole) {
    const conflict = projectMemberConflict(employee.id, existingMembers, newMembers);
    if (conflict) {
      setError(conflict);
      return;
    }
    setNewMembers(current => [
      ...current,
      {
        key: nextProjectDraftKey('member'),
        employeeId: employee.id,
        employeeName: employee.label,
        role,
      },
    ]);
    setPendingEmployee(null);
    setPendingRole(null);
    setError('');
  }

  function selectRole(role: ProjectMemberRole) {
    setPendingRole(role);
    if (pendingEmployee) addMember(pendingEmployee, role);
  }

  function commitPendingMember() {
    if (!pendingEmployee) {
      setError('Select an employee.');
      return;
    }
    if (!pendingRole) {
      setError('Select a role for the chosen employee.');
      return;
    }
    addMember(pendingEmployee, pendingRole);
  }

  function removeNewMember(key: string) {
    setNewMembers(current => current.filter(member => member.key !== key));
  }

  async function submit() {
    if (busy || !project) return;
    if (pendingEmployee && !pendingRole) {
      setError('Select a role for the chosen employee.');
      return;
    }
    if (newMembers.length === 0) {
      setError('Add at least one new member before saving.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await runOperation('update', () =>
        addProjectMembers(project.projectId, toProjectMembersAddPayload(newMembers)),
      );
      navigation.goBack();
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to add the members.'));
    } finally {
      setBusy(false);
    }
  }

  if (!canEdit) return <UnauthorizedScreen />;

  const title = project?.projectName ?? routeProjectName ?? 'Project';

  return (
    <PermissionGuard
      permission={PERMISSIONS.PROJECT_UPDATE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen webLayout="narrow" scroll keyboardSafe>
        <ScreenHeader title="Edit project" onBack={navigation.goBack} />
        <View style={styles.body}>
          {loading && !project ? (
            <Text style={themed.loading}>Loading project…</Text>
          ) : loadError && !project ? (
            <ErrorState message={loadError} onRetry={loadProject} />
          ) : project ? (
            <>
              <AppCard style={styles.card}>
                <Text style={themed.section}>Project details</Text>
                <View style={styles.field}>
                  <Text style={themed.label}>Project name</Text>
                  <Text style={themed.value}>{title}</Text>
                </View>
                {project.description ? (
                  <View style={styles.field}>
                    <Text style={themed.label}>Summary</Text>
                    <Text style={themed.value}>{project.description}</Text>
                  </View>
                ) : null}
                <View style={styles.field}>
                  <Text style={themed.label}>Created</Text>
                  <Text style={themed.value}>{formatDate(project.createdAt)}</Text>
                </View>
              </AppCard>

              <AppCard style={styles.card}>
                <Text style={themed.section}>Current members</Text>
                {existingMembers.length === 0 ? (
                  <Text style={themed.hint}>No members are assigned yet.</Text>
                ) : (
                  existingMembers.map(member => (
                    <View key={member.employeeId} style={styles.memberRow}>
                      <Text style={themed.memberName}>{member.employeeName}</Text>
                      <Text style={themed.memberRole}>
                        {projectMemberRoleLabel(member.memberRole)}
                      </Text>
                    </View>
                  ))
                )}
              </AppCard>

              <AppCard style={styles.card}>
                <Text style={themed.section}>Add members</Text>
                <Text style={themed.hint}>
                  Select an employee, then assign a role. Current members stay assigned.
                  {teamOnly
                    ? ' Only employees in your team can be assigned, including you.'
                    : ''}
                </Text>
                <SearchableEntitySelector
                  label="Employee"
                  selectedValue={pendingEmployee?.id ?? null}
                  selectedLabel={pendingEmployee?.label ?? null}
                  loadOptions={loadOptions}
                  onSelect={option => {
                    setPendingEmployee(option);
                    setPendingRole(null);
                    setError('');
                  }}
                  onClear={() => {
                    setPendingEmployee(null);
                    setPendingRole(null);
                  }}
                  placeholder="Search employee"
                  disabled={busy}
                />
                {pendingEmployee ? (
                  <View style={styles.roleBlock}>
                    <Text style={themed.label}>Role</Text>
                    <View style={styles.chipRow}>
                      {PROJECT_MEMBER_ROLES.map(role => {
                        const selected = pendingRole === role.value;
                        return (
                          <Pressable
                            key={role.value}
                            accessibilityRole="button"
                            accessibilityLabel={`Select ${role.label} role`}
                            onPress={() => selectRole(role.value)}
                            style={selected ? themed.chipActive : themed.chip}
                          >
                            <Text
                              style={selected ? themed.chipTextActive : themed.chipText}
                            >
                              {role.label}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>
                ) : null}
                <AppButton
                  title="+ Add member"
                  variant="secondary"
                  onPress={commitPendingMember}
                  disabled={busy}
                />

                {newMembers.length > 0 ? (
                  <View style={styles.listBlock}>
                    <Text style={themed.label}>New members to save</Text>
                    {newMembers.map(member => (
                      <View key={member.key} style={styles.memberRow}>
                        <Text style={themed.memberName}>{member.employeeName}</Text>
                        <Text style={themed.memberRole}>
                          {projectMemberRoleLabel(member.role)}
                        </Text>
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={`Remove ${member.employeeName}`}
                          onPress={() => removeNewMember(member.key)}
                          disabled={busy}
                        >
                          <Text style={themed.remove}>Remove</Text>
                        </Pressable>
                      </View>
                    ))}
                  </View>
                ) : null}
              </AppCard>

              {error ? (
                <Text accessibilityRole="alert" style={themed.error}>
                  {error}
                </Text>
              ) : null}
              <AppButton
                title={busy ? 'Saving…' : 'Save changes'}
                onPress={submit}
                disabled={busy}
                loading={busy}
              />
              <AppButton
                title="Cancel"
                variant="secondary"
                onPress={() => navigation.goBack()}
                disabled={busy}
              />
            </>
          ) : null}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.md, paddingBottom: spacing.xl },
  card: { gap: spacing.md },
  field: { gap: spacing.xs },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  roleBlock: { gap: spacing.sm },
  listBlock: { gap: spacing.sm },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
});
